import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { sortBy } from 'underscore';

import { addQueryParams, getWindowOrigin } from 'rev-shared/util/Util.Service';
import { extractQueryParamsFromHash } from 'rev-shared/util/UrlUtil';
import { isString, deepMergeObjects } from 'rev-shared/util';

import {
	IRevConnectInfo,
	IRevConnectLoggingInfo
} from '@vbrick/vbrick-rev-connect/src/IRevConnectInfo';
import { Subscription } from 'rxjs';

import { ThemeService } from 'rev-portal/branding/Theme.Service';

import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { DualPlaybackSourceTypes } from 'rev-shared/media/VideoSourceType';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';

import { IVbPlaybackTokenConfig, HlsUtilService, LanguageNames, SupportedPlaybacksService, VG_CONFERENCE_DUAL_PLAYBACK_EXTENSION } from '@vbrick/vbrick-player';

import { MimeTypes } from './MimeTypes';
import { IModernPlayback } from './IModernPlayback';
import { getPlaybackTokenConfig, removeWithCredParam } from './PlaybackTokenUtility';
import { ResourceType } from './ResourceType';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { StreamType } from './StreamType';
import { VbTranslateService } from 'rev-shared/util/VbTranslate.Service';
import { playerTranslationKeys } from './Translations';

const PLAYER_CSS_VARIABLE = {
	accentColor: '--player-accent-color'
};

export enum StreamProtocol {
	FLV = 'FLV',
	H264 = 'H264',
	HLS = 'HLS',
	MP4 = 'MP4',
	MULTICAST = 'Multicast',
	RTMP = 'RTMP'
}

export interface IModernSubtitle {
	label?: string;
	src: string;
	language: string;
}

export interface IPlaybackCfg {
	player?: string;
	url?: string;
	srcObject?: any;
	videoFormat: string;
	isDefault?: boolean;
	revConnectInfo?: any;
	qValue?: any;
	selected?: boolean;
	tokenConfig?: IVbPlaybackTokenConfig;
	streamType?: StreamType;
	fileInfo?: { isC2paSigned?: boolean }
}

const REV_CONNECT_TOKEN_RESOURCE_AUD: string = 'rev_connect';
const tokenRenewalIntervalInMins: number = 60;

@Injectable({
	providedIn: 'root'
})
export class VideoPlayerAdapterService {
	public readonly playerTranslations = Object.freeze(this.VbTranslateService.translateObj(playerTranslationKeys));
	private playerSubscription: Subscription;

	constructor(
		private readonly HlsUtilService: HlsUtilService,
		private readonly SupportedPlaybacks: SupportedPlaybacksService,
		private readonly UserContext: UserContextService,
		private readonly http: HttpClient,
		private readonly ThemeService: ThemeService,
		private readonly MediaFeatures: MediaFeaturesService,
		private readonly VbTranslateService: VbTranslateService
	) {}

	public convertToModernPlayerPlaybacks(resourceId: string, resourceType: ResourceType, playbacks: IPlaybackCfg[], isLive: boolean, videoSourceType: string, is360Enabled: boolean = false, revConnectLoggingInfo?: IRevConnectLoggingInfo, disableDualPlayback?: boolean): IModernPlayback[] {
		let modernPlaybacks =
			playbacks
				.filter(playback => playback.player !== 'Vbrick') // filter out playbacks for the legacy player
				.map(playback => {
					let src = playback.url || '';
					let tokenConfig;

					if (!playback.tokenConfig) {
						tokenConfig = getPlaybackTokenConfig(resourceId, resourceType, src);
						src = removeWithCredParam(src);
					} else {
						tokenConfig = playback.tokenConfig;
					}

					const type = playback.srcObject
						? MimeTypes.MP4
						: this.getModernPlayerPlaybackType(playback.videoFormat, src);
					const token = this.UserContext.getUser().token;
					return {
						is360Enabled,
						isDefault: playback.isDefault,
						isLive,
						label: this.getPlaybackLabel(playback, type),
						originalSrc: src,
						qValue: playback.qValue,
						revConnectInfo: this.shapeRevConnectInfo(resourceId, playback.revConnectInfo, revConnectLoggingInfo, src),
						//for embed type of playback, user uses tk param for authentication and to make it play on mobile, Zoom's desktop app,
						// or other than default playback sources tk needs to be appended on the playback url as query string.
						src: src.startsWith(getWindowOrigin()) ? this.addTokenInQueryParam(src, token?.accessToken) : src,
						srcObject: playback.srcObject,
						type,
						streamType: playback.streamType,
						tokenConfig,
						authHeader: token?.accessToken && `vbrick ${token.accessToken}`,
						isC2paEligible: resourceType === ResourceType.Video && !isLive && (playback.fileInfo == null || playback.fileInfo.isC2paSigned)
					};
				});

		//if video conference source, prepend dual entries for all m3u8 entires (this is a player-specific interpretation of standard hls)
		if (!disableDualPlayback && DualPlaybackSourceTypes.includes(videoSourceType)) {
			const dualPlaybacks = modernPlaybacks
				.filter(playback => playback.src.includes('.m3u8'))
				.map(hlsPlayback => Object.assign({}, hlsPlayback, {
					type: MimeTypes.DUAL,
					src: hlsPlayback.src.replace('.m3u8', VG_CONFERENCE_DUAL_PLAYBACK_EXTENSION)
				}));

			modernPlaybacks = dualPlaybacks.concat(modernPlaybacks);
		}

		return modernPlaybacks;
	}

	public convertToModernSubtitles(subtitles: Array<{ languageCode: string; src: string }>): IModernSubtitle[] {
		return sortBy(subtitles?.map(subtitle => {
			return {
				label: LanguageNames[subtitle.languageCode],
				src: subtitle.src,
				language: subtitle.languageCode
			};
		}), 'language');
	}

	private getModernPlayerPlaybackType(videoFormat: string, src: string): MimeTypes {
		const isRtmp = src.startsWith('rtmp://');

		if (this.HlsUtilService.isMulticast(src, null)) {
			videoFormat = this.HlsUtilService.TYPE_VBRICK_MULTICAST;
		} else if (isRtmp || src.startsWith('rtmfp://') || src.includes('.f4m')) {
			return null;
		} else if (src.includes('.m3u8')) { //back-end returns the wrong format for HLS
			videoFormat = 'HLS';
		} else if (src.includes('.flv') || src.includes('.f4v')) {
			videoFormat = 'FLV';
		} else if (src.includes('.m4v')) { //mpeg4-part2
			videoFormat = 'Mpeg4';
		}

		if (!isString(videoFormat)) {
			return;
		}

		switch (videoFormat.trim().toLowerCase()) {
			case 'h264':
			case 'mpeg4':
				return MimeTypes.MP4;

			case 'hls':
				return MimeTypes.HLS;

			case 'flv':
				return MimeTypes.FLV;

			default:
				return null;
		}
	}

	private getStreamProtocol(videoUrl: string, videoFormat: string): StreamProtocol {
		videoUrl = videoUrl.trim().toLowerCase();
		videoFormat = videoFormat?.trim().toLowerCase();

		if (this.SupportedPlaybacks.isMulticastSource(videoUrl, '')){
			return StreamProtocol.MULTICAST;
		} else if(videoUrl.startsWith('rtmp://')){
			return StreamProtocol.RTMP;
		}

		const mimeType = this.getModernPlayerPlaybackType(videoFormat, videoUrl);

		switch (mimeType) {
			case MimeTypes.MP4:
				return StreamProtocol.MP4;
			case MimeTypes.HLS:
				return StreamProtocol.HLS;
			case MimeTypes.FLV:
				return StreamProtocol.FLV;
			default:
				return null;
		}
	}

	public getPlaybackForUrl(url: string, modernPlaybacks: any[], rawPlaybacks: any[], isRevConnect: boolean = false): any {
		const modernPlayback = (modernPlaybacks || []).find(playback => url === playback.src && !!playback.revConnectInfo === isRevConnect); //compensate for query token and any other modifications

		if (modernPlayback) {
			const playback = (rawPlaybacks || []).find(playback => modernPlayback.originalSrc === playback.url && !!playback.revConnectInfo === isRevConnect);
			const streamProtocol = this.getStreamProtocol(url, playback?.videoFormat);

			//return a copy of the original playback with the streamProtocol corrected
			return Object.assign({}, playback, { streamProtocol });
		}

		return null;
	}

	public setPlayerStyle(element: HTMLElement, accentColor?: string): void {
		if (accentColor) {
			element.style.setProperty(PLAYER_CSS_VARIABLE.accentColor, accentColor);
			return;
		}

		this.playerSubscription = this.ThemeService.brandingSettings$
			.subscribe(brandingSettings => element.style.setProperty(PLAYER_CSS_VARIABLE.accentColor, brandingSettings.themeSettings.accentColor));
	}

	public cleanup(): void {
		this.playerSubscription?.unsubscribe();
	}

	private getPlaybackLabel(playback: any, type: MimeTypes): string {
		if (type === MimeTypes.HLS) {
			return this.playerTranslations.hlsPlaybackMenuOption;
		} else if (playback.label === '-') {
			return this.playerTranslations.originalPlaybackMenuOption;
		}

		return playback.label;
	}

	private shapeRevConnectInfo(resourceId: string, revConnectInfo: IRevConnectInfo, loggingInfoInput: IRevConnectLoggingInfo, originalSrc: string): any {
		if (!revConnectInfo) {
			return undefined;
		}
		//This is only needed in qa env to test rev connect performance.
		const rcFromUrl = this.MediaFeatures.accountFeatures?.enableRevTestModeFeatures
			&& this.readRevConnectInfoFromUrl();

		if (rcFromUrl) {
			revConnectInfo = deepMergeObjects(revConnectInfo, rcFromUrl);
		}

		const loggingInfo: IRevConnectLoggingInfo = { ...loggingInfoInput };

		if (loggingInfo && loggingInfo.remoteLoggingConfig) {
			loggingInfo.remoteLoggingConfig.context.originUrl = originalSrc;
		}

		const output = {
			...revConnectInfo,
			loggingInfo
		};

		output.jwtToken.getToken = () => this.getRevConnectSignalingToken(resourceId, revConnectInfo.zoneInfo.zoneId);

		return output;
	}

	private getRevConnectSignalingToken(resourceId: string, zoneId: string): Promise<string> {
		return lastValueFrom(this.http.get<{ token: string }>('/auth/token', {
			params: {
				aud: REV_CONNECT_TOKEN_RESOURCE_AUD,
				resourceId,
				zoneId
			}
		}))
			.then(result => result.token);
	}

	private readRevConnectInfoFromUrl(): any {
		const { rcInfo } = extractQueryParamsFromHash();
		return rcInfo ? JSON.parse(window.atob(rcInfo)) : undefined;
	}

	public addTokenInQueryParam(url: string, token?: string): string {

		if (!token) {
			return url;
		}

		return addQueryParams(url, { tk: token });
	}
}
